import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { api } from "@/api";
import { isPaidOrderStatus } from "@/components/profile/order-view";
import { takeOrderTokenFromHash } from "@/api/access-tokens";
import { Button } from "@/components/ui/button";
import { loadCloudPayments } from "@/lib/cloudpayments";
import { useFormatPrice } from "@/lib/use-format-price";

/**
 * Оплата одного заказа по ссылке: `/pay/<id заказа>`.
 *
 * Нужна мобильному приложению. Виджет CloudPayments — браузерный скрипт: он
 * работает через DOM и открывает 3-D Secure своим окном, поэтому в React Native
 * его нет и быть не может. Приложение создаёт заказ обычной ручкой и открывает
 * эту страницу ссылкой — платит покупатель здесь, тем же виджетом, что и веб.
 *
 * Заказ закрывает сервер, а не эта страница: `POST /orders/:id/confirm`
 * спрашивает у CloudPayments, есть ли списание по счёту, плюс приходит вебхук
 * эквайера. Поэтому возвращаться в приложение не обязательно — статус доедет
 * сам. Страница лишь опрашивает заказ, чтобы показать исход.
 *
 * Оплатить можно, зная только id (так сюда приводит приложение): шлюз отдаёт
 * по нему платёжный вид заказа без контактов и адреса. Ссылка от сервера
 * (`payUrl`) несёт ещё токен заказа во фрагменте `#t=…` — с ним заказ виден
 * целиком (почта уходит в виджет как accountId).
 */
export function PayPage() {
  const {
    orderId = ""
  } = useParams();
  // Токен из `#t=` — до первого запроса заказа, поэтому в инициализаторе.
  useState(() => takeOrderTokenFromHash(orderId));
  const fmt = useFormatPrice();
  const [state, setState] = useState<"idle" | "paying" | "paid" | "declined" | "timeout" | "stale">("idle");
  const pollRef = useRef<{
    cancelled: boolean;
  } | null>(null);
  const order = useQuery({
    queryKey: ["pay", "order", orderId],
    queryFn: () => api.orders.get(orderId),
    enabled: Boolean(orderId),
    retry: false
  });
  const config = useQuery({
    queryKey: ["payments", "config"],
    queryFn: () => api.payments.config()
  });

  // Опрос статуса: `confirm` держит заказ в «ждёт оплаты», пока у эквайера нет
  // списания, и закрывает его, когда оно появилось. Вебхук делает то же самое
  // со стороны сервера — опрос нужен только чтобы показать исход здесь.
  const startPolling = useCallback(() => {
    if (pollRef.current) pollRef.current.cancelled = true;
    const token = {
      cancelled: false
    };
    pollRef.current = token;
    const deadline = Date.now() + 4 * 60 * 1000;
    const tick = async () => {
      if (token.cancelled) return;
      try {
        const next = await api.orders.confirm(orderId);
        if (token.cancelled) return;
        // Индивидуальный заказ после оплаты сразу «Подтверждён» (fd-order-29).
        if (isPaidOrderStatus(next.status)) return setState("paid");
        if (next.status === "declined") return setState("declined");
      } catch (err) {
        // Продавец изменил заказ, и прежний счёт аннулирован: шлюз не
        // проводит оплату старой суммы. Опрашивать дальше бессмысленно.
        if (isStaleInvoice(err)) return setState("stale");
        // Сеть моргнула — пробуем ещё раз.
      }
      if (token.cancelled) return;
      if (Date.now() > deadline) return setState("timeout");
      setTimeout(tick, 2500);
    };
    void tick();
  }, [orderId]);
  useEffect(() => () => {
    if (pollRef.current) pollRef.current.cancelled = true;
  }, []);

  // Возврат с платёжной страницы Робокассы: она приводит покупателя обратно с
  // собственными параметрами (InvId и подпись). Верить им на слово нельзя —
  // исход всё равно спрашиваем у сервера, но опрос запускаем сразу, не
  // заставляя нажимать «Оплатить» второй раз.
  // biome-ignore lint/correctness/useExhaustiveDependencies: запускается один раз на возврате
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("InvId") || params.get("from") === "robokassa") {
      setState("paying");
      startPolling();
    }
  }, []);
  const data = order.data;
  const amount = data?.payableTotal ?? data?.total ?? 0;
  const alreadyPaid = data ? data.status !== "pending" : false;
  const pay = async () => {
    if (!data || !config.data) return;
    // Страницу могли открыть до того, как продавец изменил заказ: сверяем
    // сумму со свежим заказом, чтобы не платить по аннулированному счёту.
    const fresh = await order.refetch();
    const freshAmount = fresh.data?.payableTotal ?? fresh.data?.total ?? amount;
    if (fresh.data && freshAmount !== amount) {
      setState("stale");
      return;
    }
    setState("paying");
    // Провайдер «mock» — стенд без эквайринга: там заказ закрывается самим
    // подтверждением, виджет открывать нечем и незачем.
    if (config.data.provider === "mock" || amount <= 0) {
      startPolling();
      return;
    }
    // Робокасса — редирект-провайдер: оплата идёт на её странице, а не в
    // виджете. Ссылку с подписью выдаёт шлюз; возвращаемся сюда же по
    // SuccessURL, и опрос ниже покажет исход.
    if (config.data.mode === "redirect") {
      try {
        const {
          url
        } = await api.payments.url(orderId);
        window.location.assign(url);
      } catch {
        setState("declined");
      }
      return;
    }
    try {
      const cp = await loadCloudPayments();
      new cp.CloudPayments().pay("charge", {
        publicId: config.data.publicId ?? "",
        description: `Заказ ${data.id}`,
        amount,
        currency: config.data.currency,
        invoiceId: data.id,
        accountId: data.contact.email || undefined,
        skin: "mini",
        data: {
          orderId: data.id
        }
      }, {
        onSuccess: () => startPolling(),
        onFail: () => setState("declined")
      });
      // Виджет не всегда зовёт onSuccess (например, если окно закрыли после
      // успешного списания), поэтому опрос запускаем сразу.
      startPolling();
    } catch {
      setState("declined");
    }
  };
  if (!orderId) return <Shell title="Заказ не указан" />;
  if (order.isPending) return <Shell title="Загружаем заказ…" spinner />;
  if (order.isError || !data) return <Shell title="Заказ не найден" note="Проверьте ссылку — возможно, она устарела. Заказ виден в приложении в разделе «Мои заказы»." tone="error" />;
  if (state === "paid" || alreadyPaid) return <Shell title="Оплата прошла" note="Заказ оплачен. Можно закрыть страницу и вернуться в приложение — статус там уже обновился." tone="success" />;
  if (state === "declined") return <Shell title="Платёж не прошёл" note="Банк отклонил операцию или окно оплаты закрыли. Деньги не списаны — попробуйте ещё раз." tone="error">
				<Button block className="mt-4" onClick={() => void pay()}>
					Попробовать снова
				</Button>
			</Shell>;

  // Счёт аннулирован: продавец изменил заказ. Новый счёт — это та же страница
  // со свежей суммой, поэтому кнопка перечитывает заказ и возвращает к оплате.
  if (state === "stale") return <Shell title="Счёт аннулирован: продавец изменил заказ" note={`Прежний счёт больше не принимается к оплате. Новая сумма к оплате: ${fmt(amount)}. Если по прежнему счёту деньги уже списались, мы их вернём - оформлять ничего не нужно.`} tone="error">
				<Button block className="mt-4" onClick={() => {
      void order.refetch().then(() => setState("idle"));
    }}>
					Перейти к новому счёту
				</Button>
			</Shell>;
  if (state === "timeout") return <Shell title="Платёж обрабатывается" note="Банк ещё не подтвердил операцию. Закрывать страницу можно: как только подтверждение придёт, заказ закроется сам, статус будет виден в приложении." />;
  return <Shell title={`Оплата заказа ${data.id}`}>
			<dl className="mt-4 flex flex-col gap-2 text-[15px] leading-5">
				<div className="flex items-baseline justify-between gap-4">
					<dt className="text-osmix-text-2">Товаров</dt>
					<dd className="font-medium text-osmix-text-1">{data.itemCount}</dd>
				</div>
				<div className="flex items-baseline justify-between gap-4">
					<dt className="text-osmix-text-2">Доставка</dt>
					<dd className="font-medium text-osmix-text-1">{fmt(data.deliveryTotal)}</dd>
				</div>
				<div className="flex items-baseline justify-between gap-4 border-t border-osmix-gray-300 pt-2">
					<dt className="text-osmix-text-1">К оплате</dt>
					<dd className="text-[20px] font-semibold text-osmix-text-1">{fmt(amount)}</dd>
				</div>
			</dl>
			<Button block className="mt-5" disabled={state === "paying" || config.isPending} onClick={() => void pay()}>
				{state === "paying" ? "Ждём банк…" : "Оплатить"}
			</Button>
			<p className="mt-3 text-[13px] leading-[18px] text-osmix-text-2">
				Оплата проходит на стороне банка-эквайера. Данные карты на сервер OSMIX не попадают.
			</p>
		</Shell>;
}
function Shell(t0) {
  const $ = _c(19);
  const {
    title,
    note,
    tone,
    spinner,
    children
  } = t0;
  let t1;
  if ($[0] !== spinner) {
    t1 = spinner ? <Loader2 className="size-5 animate-spin text-osmix-text-2" aria-hidden="true" /> : null;
    $[0] = spinner;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tone) {
    t2 = tone === "success" ? <CheckCircle2 className="size-5 text-osmix-tangelo" aria-hidden="true" /> : null;
    $[2] = tone;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== tone) {
    t3 = tone === "error" ? <CircleAlert className="size-5 text-osmix-tangelo" aria-hidden="true" /> : null;
    $[4] = tone;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== title) {
    t4 = <h1 className="text-[20px] font-semibold leading-6 text-osmix-text-1">{title}</h1>;
    $[6] = title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className="flex items-center gap-2">{t1}{t2}{t3}{t4}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== note) {
    t6 = note ? <p className="mt-2 text-[15px] leading-5 text-osmix-text-2">{note}</p> : null;
    $[13] = note;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== children || $[16] !== t5 || $[17] !== t6) {
    t7 = <div className="mx-auto flex min-h-[60vh] w-full max-w-[420px] flex-col justify-center px-4 py-10"><div className="rounded-[20px] border border-osmix-gray-300 p-5">{t5}{t6}{children}</div></div>;
    $[15] = children;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}

/** Отказ шлюза «Счёт аннулирован…» (`STALE_INVOICE_MESSAGE`, 409). */
const isStaleInvoice = (err: unknown): boolean => err instanceof Error && /Счёт аннулирован/.test(err.message);